import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { getSupplierConcentrationData, getAvailableSupplierMinerals } from '../services/supplierConcentrationApi';

const SupplierConcentrationTreemap = ({ selectedMineral = 'Lithium' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableMinerals, setAvailableMinerals] = useState(['Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite']);
  const [selectedMineralFilter, setSelectedMineralFilter] = useState(selectedMineral);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, data: null, x: 0, y: 0 });
  
  const svgRef = useRef();
  const containerRef = useRef();

  // Load available minerals
  useEffect(() => {
    const loadAvailableMinerals = async () => {
      try {
        const minerals = await getAvailableSupplierMinerals();
        setAvailableMinerals(minerals);
      } catch (error) {
        console.error('Error loading available minerals:', error);
      }
    };
    loadAvailableMinerals();
  }, []);

  // Load supplier concentration data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const concentrationData = await getSupplierConcentrationData(selectedMineralFilter, 'Import');
        setData(concentrationData);
      } catch (error) {
        console.error('Error loading supplier concentration data:', error);
        setError('Failed to load supplier concentration data');
      } finally {
        setLoading(false);
      }
    };

    if (selectedMineralFilter) {
      loadData();
    }
  }, [selectedMineralFilter]);

  // Transform data for treemap
  const transformDataForTreemap = (data) => {
    if (!data || !data.supplier_data) {
      return { name: "Supplier Concentration", children: [] };
    }

    const suppliers = data.supplier_data.map(supplier => ({
      name: supplier.country,
      value: supplier.import_volume || supplier.market_share || 0,
      marketShare: supplier.market_share || 0,
      importVolume: supplier.import_volume || 0,
      riskLevel: supplier.risk_level || 'medium',
      hhi: supplier.hhi || 0
    }));

    return {
      name: "Supplier Concentration",
      children: suppliers
    };
  };

  // Render treemap
  const renderTreemap = () => {
    if (!svgRef.current || !data) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = 400;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    const treemapData = transformDataForTreemap(data);
    
    if (!treemapData.children || treemapData.children.length === 0) {
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", height / 2)
        .attr("text-anchor", "middle")
        .attr("fill", "#6b7280")
        .text("No supplier data available");
      return;
    }

    // Create treemap layout
    const treemap = d3.treemap()
      .size([width, height])
      .padding(2)
      .round(true);

    const root = d3.hierarchy(treemapData)
      .sum(d => d.value)
      .sort((a, b) => b.value - a.value);

    treemap(root);

    // Color scale based on market share
    const colorScale = d3.scaleSequential(d3.interpolateBlues)
      .domain([0, d3.max(treemapData.children, d => d.marketShare) || 1]);

    // Create cells
    const cells = svg.selectAll("g")
      .data(root.leaves())
      .enter()
      .append("g")
      .attr("transform", d => `translate(${d.x0},${d.y0})`);

    // Add rectangles
    cells.append("rect")
      .attr("width", d => d.x1 - d.x0)
      .attr("height", d => d.y1 - d.y0)
      .attr("fill", d => colorScale(d.data.marketShare))
      .attr("stroke", "#ffffff")
      .attr("stroke-width", 1)
      .style("cursor", "pointer")
      .on("mouseover", function(event, d) {
        setHoveredCell(d);
        setTooltip({
          visible: true,
          data: d.data,
          x: event.pageX,
          y: event.pageY
        });
      })
      .on("mouseout", function() {
        setHoveredCell(null);
        setTooltip({ visible: false, data: null, x: 0, y: 0 });
      });

    // Add text labels
    cells.append("text")
      .attr("x", d => (d.x1 - d.x0) / 2)
      .attr("y", d => (d.y1 - d.y0) / 2)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", "#ffffff")
      .attr("font-size", d => Math.min((d.x1 - d.x0) / 8, (d.y1 - d.y0) / 4, 12))
      .text(d => d.data.name)
      .style("pointer-events", "none");
  };

  useEffect(() => {
    if (data) {
      renderTreemap();
    }
  }, [data]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (data) {
        renderTreemap();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-700 rounded-lg">
        <div className="text-center">
          <div className="text-2xl mb-2 animate-spin">🔄</div>
          <div className="text-gray-300">Loading supplier concentration data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-700 rounded-lg">
        <div className="text-center">
          <div className="text-2xl mb-2">⚠️</div>
          <div className="text-gray-300">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-300">Mineral:</label>
          <select
            value={selectedMineralFilter}
            onChange={(e) => setSelectedMineralFilter(e.target.value)}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {availableMinerals.map(mineral => (
              <option key={mineral} value={mineral}>{mineral}</option>
            ))}
          </select>
        </div>
        
        {data && (
          <div className="text-sm text-gray-400">
            <span className="font-medium">HHI Index:</span> {data.hhi_index?.toFixed(2) || 'N/A'}
            <span className="ml-4 font-medium">Suppliers:</span> {data.supplier_data?.length || 0}
          </div>
        )}
      </div>

      {/* Treemap Visualization */}
      <div ref={containerRef} className="w-full">
        <svg ref={svgRef} className="w-full"></svg>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between text-sm text-gray-400">
        <div className="flex items-center space-x-4">
          <span>Low Concentration</span>
          <div className="flex space-x-1">
            {[0, 0.25, 0.5, 0.75, 1].map((value, index) => (
              <div
                key={index}
                className="w-4 h-4 rounded"
                style={{ backgroundColor: d3.interpolateBlues(value) }}
              />
            ))}
          </div>
          <span>High Concentration</span>
        </div>
        <div className="text-xs">
          Market share by supplier country
        </div>
      </div>

      {/* Tooltip */}
      {tooltip.visible && tooltip.data && (
        <div
          className="fixed z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg pointer-events-none"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y - 10,
          }}
        >
          <div className="font-semibold">{tooltip.data.name}</div>
          <div>Market Share: {(tooltip.data.marketShare * 100).toFixed(1)}%</div>
          <div>Import Volume: {tooltip.data.importVolume?.toLocaleString() || 'N/A'}</div>
          <div>Risk Level: {tooltip.data.riskLevel}</div>
        </div>
      )}
    </div>
  );
};

export default SupplierConcentrationTreemap;
